import {
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  rejectUnknownKeys,
  validateOptionalString,
} from './elementFields.validation';

export const DOCUMENT_ELEMENT_KEYS = [
  'title',
  'caption',
  'file',
  'labels',
  'unavailableReason',
] as const;

const DOCUMENT_LABEL_KEYS = [
  'previousPage',
  'nextPage',
  'pagePosition',
  'loading',
  'unreadable',
  'zoomIn',
  'zoomOut',
] as const;

export const DOCUMENT_PAGE_PLACEHOLDERS = ['{page}', '{pageCount}'] as const;

function invalid(path: string, message: string): never {
  throw new Error(`Invalid descriptor: ${path} ${message}.`);
}

/**
 * The viewer reads a same-origin asset or bytes a domain hook already holds
 * (object or PDF data URL). A cross-origin or protocol-relative URL would make
 * the renderer fetch on its own, which renderers never do. Bundlers emit an
 * asset as a root path, a relative path or an absolute URL on the page's own
 * origin, so the check resolves the value against the page before comparing
 * origins.
 */
function isSameOrigin(value: string): boolean {
  const page = globalThis.location;
  if (!page) return value.startsWith('/') && !value.startsWith('//');
  try {
    return new URL(value, page.href).origin === page.origin;
  } catch {
    return false;
  }
}

function assertReadableFile(value: unknown, path: string): void {
  assertNonEmptyString(value, path);
  const heldBytes = value.startsWith('blob:') || value.startsWith('data:application/pdf');
  if (!heldBytes && !isSameOrigin(value)) {
    invalid(
      path,
      `must be a same-origin URL, an object URL or a PDF data URL ("${value}"); fetch the file in a domain hook`,
    );
  }
}

function validateLabels(value: unknown, path: string): void {
  assertRecord(value, path);
  rejectUnknownKeys(value, DOCUMENT_LABEL_KEYS, path);
  DOCUMENT_LABEL_KEYS.forEach((key) => assertNonEmptyString(value[key], `${path}.${key}`));
  const position = value.pagePosition as string;
  const missing = DOCUMENT_PAGE_PLACEHOLDERS.filter((token) => !position.includes(token));
  if (missing.length > 0) {
    invalid(`${path}.pagePosition`, `must name ${missing.join(' and ')} ("${position}")`);
  }
}

/** A document is either served (file + labels) or names why it is not. */
export function validateDocumentElement(element: Record<string, unknown>, path: string): void {
  assertNonEmptyString(element.title, `${path}.title`);
  validateOptionalString(element, 'caption', path);
  if (hasOwn(element, 'unavailableReason')) {
    if (hasOwn(element, 'file') || hasOwn(element, 'labels')) {
      invalid(path, 'carries either a served file with labels or an unavailableReason, not both');
    }
    assertNonEmptyString(element.unavailableReason, `${path}.unavailableReason`);
    return;
  }
  if (!hasOwn(element, 'file')) invalid(path, 'requires file or unavailableReason');
  assertReadableFile(element.file, `${path}.file`);
  if (!hasOwn(element, 'labels')) invalid(`${path}.labels`, 'is required with a served file');
  validateLabels(element.labels, `${path}.labels`);
}
